/**
 * Country config helpers backed by window.__COUNTRY_CONFIG__.
 */

import { detectClientEnvironment } from "../features/clientEnv";
import { getResolvedFbPixelId as getBootstrapFbPixelId } from "../features/attribution/runtime";

export interface CountryConfig {
  code: number;
  currency: string;
  siteName: string;
  channel: string;
  apiBase: string;
  lang: string;
  supportEmail?: string;
  login: {
    phone: {
      min?: number;
      max?: number;
      exact?: number;
    };
    phoneMaxLength: number;
  };
  install: {
    installLink?: string;
  };
}

export interface GlobalCountryConfig {
  countries: Record<string, CountryConfig>;
  domains: Record<string, string>;
  pidMap: Record<string, string>;
  fbPixelMap: Record<string, string>;
}

const getGlobalConfig = (): GlobalCountryConfig | null => {
  if (typeof window !== "undefined" && (window as any).__COUNTRY_CONFIG__) {
    return (window as any).__COUNTRY_CONFIG__;
  }
  return null;
};

const getCookieValue = (name: string): string => {
  if (typeof document === "undefined") return "";
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split("=");
    if (cookieName === name) return cookieValue || "";
  }
  return "";
};

export const getCurrentCountryCode = (): string | null => {
  if (typeof window === "undefined") return null;

  const config = getGlobalConfig();
  if (!config) return null;

  const hostname = window.location.hostname.toLowerCase();
  return config.domains[hostname] || "ke";
};

export const getCountryCodeByDomain = (): number => {
  const config = getCountryConfig();
  return config?.code || 254;
};

export const getCountryConfig = (): CountryConfig | null => {
  const countryCode = getCurrentCountryCode();
  if (!countryCode) return null;

  const config = getGlobalConfig();
  if (!config) return null;

  return config.countries[countryCode] || null;
};

export const getCurrentCurrency = (): string => {
  const config = getCountryConfig();
  return config?.currency || "KSh";
};

export const getCurrentApiBase = (): string => {
  const config = getCountryConfig();
  if (!config) {
    console.warn("No country config found, using default API base");
    return "";
  }
  return config.apiBase;
};

export const getCurrentChannel = (): string => {
  const config = getCountryConfig();
  if (!config) {
    console.warn("No country config found, using default channel");
    return "";
  }
  return config.channel;
};

export const getCurrentLang = (): string => {
  const config = getCountryConfig();
  return config?.lang || "en_US";
};

export const getCurrentSiteName = (): string => {
  const config = getCountryConfig();
  return config?.siteName || "Casino";
};

export const getCurrentLoginConfig = () => {
  const config = getCountryConfig();
  return config?.login || null;
};

export const getResolvedFbPixelId = (): string => {
  return getBootstrapFbPixelId();
};

export const shouldShowInstallTopBanner = (): boolean => {
  if (typeof window === "undefined") return false;

  const config = getCountryConfig();
  if (!config || !config.install) return false;

  // Explicitly hide when the page is opened inside the native app WebView.
  const env = detectClientEnvironment();
  if (env.isNativeWebView) return false;

  // 当前区域配置了安装链接，则展示 Banner
  return !!config.install.installLink;
};

export const getCurrentInstallLink = (): string => {
  const config = getCountryConfig();
  return config?.install?.installLink || "";
};

export const getCurrentCountryInfo = () => {
  const config = getCountryConfig();
  if (!config) return null;

  return {
    code: config.code,
  };
};

export const getCurrentSupportEmail = (): string => {
  const config = getCountryConfig();
  return config?.supportEmail || "support@example.com";
};

export const getCurrentAppName = (): string => {
  const countryCode = getCurrentCountryCode()?.toUpperCase();
  const appNameMap: Record<string, string> = {
    KE: "9BETIN",
    GH: "PLAYSIKA",
    CI: "PLAYBARA",
    SN: "GAMEYOON",
    TG: "GAMEDZRA",
    BJ: "GAMESIKA",
    BF: "GAMESEEDA",
    ZM: "WINWANGA",
    EG: "LUCKYKASB",
    NG: "LUCKYCACA",
    MX: "WINZAGO",
    TZ: "GAMEPESA",
  };
  return appNameMap[countryCode || "KE"] || appNameMap["KE"];
};

export const convertInviteUrlToCurrentHost = (originalUrl: string): string => {
  if (!originalUrl) return originalUrl;

  try {
    if (typeof window !== "undefined") {
      const currentHostname = window.location.hostname;
      const currentProtocol = window.location.protocol;

      const getMainDomain = (hostname: string): string => {
        if (hostname.startsWith("www.")) {
          return hostname;
        }

        const parts = hostname.split(".");
        if (parts.length >= 3) {
          const mainDomain = parts.slice(-2).join(".");
          return `www.${mainDomain}`;
        }

        return `www.${hostname}`;
      };

      const wwwHostname = getMainDomain(currentHostname);
      const url = new URL(originalUrl);
      url.protocol = currentProtocol;
      url.hostname = wwwHostname;

      return url.toString();
    }
  } catch (error) {
    console.warn("Failed to convert invite URL:", error);
  }

  return originalUrl;
};

export default {
  getCountryConfig,
  getCurrentCountryCode,
  getCurrentCurrency,
  getCurrentApiBase,
  getCurrentChannel,
  getCurrentLang,
  getCurrentSiteName,
  getCurrentSupportEmail,
  getCurrentLoginConfig,
  getResolvedFbPixelId,
  shouldShowInstallTopBanner,
  getCurrentInstallLink,
  getCurrentCountryInfo,
  getCountryCodeByDomain,
  convertInviteUrlToCurrentHost,
  getCurrentAppName,
};
